import { InlineLoading, InlineNotification, Tag } from '@carbon/react';
import classNames from 'classnames';
import { generatePath } from 'react-router-dom';
import routes from '~/routes';
import { EntityLink } from 'components/entity-link';
import { PlainLink } from 'components/link';
import { formatCellValue } from 'components/data-table';
import { EMPTY_CELL } from 'utils/empty-cell';
import { entityRefLabel } from 'utils/entity-id';
import { useDecisionDetail } from './hooks';
import { ReasonCell } from './reason-cell';
import { matchResolutionLabel } from './match-resolution';
import { OutcomeTag } from './outcome-tag';
import { MetaItem } from '../meta-item';
import type {
  AutoMatchCandidate,
  AutoMatchDecisionDetail,
  AutoMatchGateRow,
} from './types';
import shared from '../admin-log.module.scss';
import styles from './decision-log.module.scss';

const GATE_RESULT_LABEL: Record<AutoMatchGateRow['result'], string> = {
  pass: 'Pass',
  fail: 'Fail',
  disabled: 'Off',
  not_evaluated: 'N/A',
};

const CHIP_CLASS: Record<AutoMatchGateRow['result'], string> = {
  pass: styles.chipPass,
  fail: styles.chipFail,
  disabled: styles.chipDisabled,
  not_evaluated: styles.chipDisabled,
};

/**
 * A roster row's identity: its alias group when collapsed into one, else its
 * entity. A roster lists each group or standalone entity once.
 * @param {AutoMatchCandidate} candidate - The roster row
 * @return {string} Its key
 */
const candidateKey = (candidate: AutoMatchCandidate) =>
  candidate.aliasGroupId === null
    ? `entity-${candidate.entityId}`
    : `group-${candidate.aliasGroupId}`;

/**
 * The gate's per-guardrail rundown for one decision.
 * @param {object} props - The trace rows
 * @param {AutoMatchGateRow[]} props.rows - One row per guardrail checked
 * @return {JSX.Element} The trace table, or a note when the gate did not run
 */
function GateTrace({ rows }: { rows: AutoMatchGateRow[] }) {
  if (rows.length === 0)
    return (
      <p className={shared.muted}>
        No gate trace — the gate did not run for this decision.
      </p>
    );
  return (
    <table className={styles.dataTable}>
      <thead>
        <tr>
          <th>Check</th>
          <th>Requirement</th>
          <th>Observed</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((g) => (
          <tr
            key={g.guardrailId}
            className={classNames({
              [styles.gateRowFail]: g.result === 'fail',
            })}
          >
            <td className={styles.gateId}>{g.guardrailId}</td>
            <td>{g.requirement}</td>
            <td className={styles.mono}>{g.observed}</td>
            <td>
              <span
                className={classNames(
                  styles.chip,
                  CHIP_CLASS[g.result] ?? styles.chipDisabled
                )}
              >
                {GATE_RESULT_LABEL[g.result] ?? g.result}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * One loaded decision's full detail: metadata, gate trace, candidate roster.
 * @param {object} props - The decision
 * @param {AutoMatchDecisionDetail} props.decision - The loaded decision
 * @return {JSX.Element} The detail body
 */
function DetailBody({ decision }: { decision: AutoMatchDecisionDetail }) {
  return (
    <>
      <div className={shared.detailMeta}>
        <MetaItem label="Outcome">
          <OutcomeTag outcome={decision.outcome} />
        </MetaItem>
        <MetaItem
          label={decision.outcome === 'auto_confirmed' ? 'Basis' : 'Reason'}
        >
          <ReasonCell row={decision} />
        </MetaItem>
        <MetaItem label="Document">
          {decision.documentId ? (
            <PlainLink
              href={generatePath(routes.documentPath, {
                id: decision.documentId,
              })}
              openInNewTab
            >
              #{decision.documentId} ↗
            </PlainLink>
          ) : (
            EMPTY_CELL
          )}
        </MetaItem>
        <MetaItem label="County">{decision.county || EMPTY_CELL}</MetaItem>
        <MetaItem label="Staged entity">
          <EntityLink id={decision.entityId} name={decision.entityName} />
        </MetaItem>
        <MetaItem label="Matched to">
          <EntityLink
            id={decision.matchedEntityId}
            name={decision.matchedEntityName}
            fallback={EMPTY_CELL}
          />
        </MetaItem>
        <MetaItem label="Name match / action">
          {matchResolutionLabel(decision) ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label="Scores (C1 / C2)">
          {decision.c1 ?? EMPTY_CELL} / {decision.c2 ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label="Config version">
          {decision.configVersion ?? 'default'}
        </MetaItem>
        <MetaItem label="Ruleset version">
          {decision.guardrailRulesetVersion ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label="Recorded">
          {formatCellValue(decision.createdAt, { type: 'datetime' })}
        </MetaItem>
        {decision.reconciliation && (
          <MetaItem label="Reconciliation">
            #{decision.reconciliation.id}
          </MetaItem>
        )}
        {decision.undone && (
          <>
            <MetaItem label="Undone by">
              {decision.undoneBy || 'Unknown'}
              {decision.undoneAt
                ? ` · ${formatCellValue(decision.undoneAt, { type: 'datetime' })}`
                : ''}
            </MetaItem>
            <MetaItem label="Re-resolved to">
              <EntityLink
                id={decision.redoneToId}
                name={decision.redoneToName}
                fallback={
                  <span className={shared.muted}>Not yet re-resolved</span>
                }
              />
            </MetaItem>
          </>
        )}
      </div>

      {decision.justificationStatement && (
        <p className={styles.justification}>
          {decision.justificationStatement}
        </p>
      )}

      <h3 className={shared.sectionTitle}>Gate rundown</h3>
      <GateTrace rows={decision.guardrailTrace} />

      <h3 className={shared.sectionTitle}>
        Candidate roster ({decision.candidates.length})
      </h3>
      {decision.candidates.length === 0 ? (
        <p className={shared.muted}>No candidates were scored.</p>
      ) : (
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Score</th>
              <th>Name</th>
              <th>Type</th>
              <th>Group</th>
              <th>Flag</th>
            </tr>
          </thead>
          <tbody>
            {decision.candidates.map((c) => (
              <tr key={candidateKey(c)}>
                <td className={styles.mono}>
                  {c.composite === null ? EMPTY_CELL : Math.round(c.composite)}
                </td>
                <td>{entityRefLabel(c.name, c.entityId)}</td>
                <td>{(c.type || '').replace('Entity', '') || EMPTY_CELL}</td>
                <td className={styles.mono}>
                  {c.aliasGroupId ?? c.entityId ?? EMPTY_CELL}
                </td>
                <td>
                  {c.flagSeverity ? (
                    <Tag
                      type={c.flagSeverity === 'warning' ? 'red' : 'gray'}
                      size="sm"
                    >
                      {c.flagSeverity}
                    </Tag>
                  ) : (
                    <span className={shared.muted}>{EMPTY_CELL}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/**
 * Expanded-row body: lazily loads and renders one decision's full detail.
 * @param {object} props - The decision to load
 * @param {number} props.decisionId - Its id
 * @return {JSX.Element} The loading, error, or loaded detail
 */
export function DecisionDetail({ decisionId }: { decisionId: number }) {
  const { data, isPending, isError } = useDecisionDetail(decisionId);

  return (
    <div className={shared.expandedDetail}>
      {isPending && <InlineLoading description="Loading decision…" />}
      {isError && (
        <InlineNotification
          kind="error"
          hideCloseButton
          lowContrast
          title="Failed to load"
          subtitle="This decision could not be loaded."
        />
      )}
      {data && <DetailBody decision={data} />}
    </div>
  );
}
